import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiPlus,
  FiX,
  FiAward,
  FiTrash2,
  FiEdit2,
  FiExternalLink,
  FiStar,
  FiShield
} from 'react-icons/fi';

// Types and Interfaces
interface Skill {
  _id: string;
  name: string;
  category: string;
  description: string;
}

interface Evidence {
  type: 'certification' | 'project' | 'work' | 'education';
  title: string;
  issuer?: string;
  date?: string;
  url?: string;
}

interface UserSkill {
  id: string;
  name: string;
  category?: string;
  description?: string;
  proficiency: number;
  yearsOfExperience?: number;
  lastUsed?: string;
  endorsements: number;
  evidence?: Evidence[];
}

interface AssessmentQuestion {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
}

interface Assessment {
  id?: string;
  _id?: string;
  skillId: string;
  skillName: string;
  level?: string;
  questions: AssessmentQuestion[];
  completed: boolean;
  score?: number;
  dateCompleted?: string;
}

interface SkillManagementProps {
  user: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    name?: string;
    avatar?: string;
  } | null;
  userSkills: UserSkill[];
  availableSkills: Skill[];
  loading: {
    skills: boolean;
    saving: boolean;
    assessment: boolean;
  };
  onRefresh: () => void;
  apiClient: any;
  searchTerm?: string;
  selectedCategory?: string;
}

const SKILL_CATEGORIES = [
  { id: 'all', name: 'All Specialties', color: '#D4AF37' },
  { id: 'professional', name: 'Professional', color: '#D4AF37' },
  { id: 'practical', name: 'Practical', color: '#4A90E2' },
  { id: 'soft', name: 'Soft Skills', color: '#9013FE' },
  { id: 'physical', name: 'Physical', color: '#7ED321' },
];

const SKILL_ICONS: Record<string, React.ReactNode> = {
  professional: <FiAward />,
  practical: <FiShield />,
  soft: <FiStar />,
  physical: <FiPlus />,
  general: <FiShield />
};

const PROFICIENCY_LEVELS = [
  { value: 1, label: 'Beginner', description: 'Just starting the journey', icon: 'academy' },
  { value: 2, label: 'Novice', description: 'Gaining foundational knowledge', icon: 'dispatch' },
  { value: 3, label: 'Intermediate', description: 'Capable and independent', icon: 'talent' },
  { value: 4, label: 'Advanced', description: 'Deep knowledge and experience', icon: 'projects' },
  { value: 5, label: 'Expert', description: 'Master of the craft', icon: 'aihub' },
];

// Re-using the USER_PROFICIENCY_LEVELS from proficiencyMap for consistency
// import { USER_PROFICIENCY_LEVELS } from '../utils/proficiencyMap'; // Not used in this component currently

const SkillCard: React.FC<{
  skill: UserSkill;
  onEdit: (skill: UserSkill) => void;
  onDelete: (id: string) => void;
  onView: (skill: UserSkill) => void;
  onAssessment: (id: string) => void;
}> = ({ skill, onEdit, onDelete, onView, onAssessment }) => {
  // const profLevel = PROFICIENCY_LEVELS.find(p => p.value === skill.proficiency); // Not used currently

  return (
    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-5 hover:border-premium-gold/30 transition-all hover:bg-white/10 group">
      <div className="flex justify-between items-start mb-4">
        <div className="flex gap-4">
          <div className="w-14 h-14 flex-shrink-0 bg-white/5 rounded-lg flex items-center justify-center border border-white/10 group-hover:border-premium-gold/20 transition-all">
            <div className="flex flex-wrap items-center justify-center gap-0.5 px-2">
              {Array.from({ length: skill.proficiency }).map((_, i) => (
                <FiStar key={i} size={10} className="text-premium-gold fill-premium-gold" />
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-lg font-bold text-white font-serif">
              {skill.name}
              {skill.endorsements > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-premium-gold text-[#1a1510]">
                  {skill.endorsements} Endorsements
                </span>
              )}
            </h3>
            <p className="text-xs text-white/40 uppercase tracking-widest mt-0.5">
              {skill.category || 'General Specialty'}
            </p>
          </div>
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onEdit(skill)}
            className="p-2 text-white/40 hover:text-premium-gold hover:bg-white/5 rounded-lg transition-all"
            title="Edit Skill"
          >
            <FiEdit2 size={14} />
          </button>
          <button
            onClick={() => onDelete(skill.id)}
            className="p-2 text-white/40 hover:text-red-400 hover:bg-white/5 rounded-lg transition-all"
            title="Remove Skill"
          >
            <FiTrash2 size={14} />
          </button>
        </div>
      </div>

      <div className="mb-4">
        <p className="text-xs text-white/60 line-clamp-2 leading-relaxed h-8">
          {skill.description || 'No description provided for this mastery.'}
        </p>
      </div>

      <div className="flex items-center justify-between mt-auto pt-4 border-t border-white/5">
        <div className="flex items-baseline gap-1">
          <span className="text-lg font-bold text-white font-serif">{skill.yearsOfExperience || 0}</span>
          <span className="text-[10px] text-white/40 uppercase tracking-widest">Yrs</span>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => onAssessment(skill.id)}
            className="px-3 py-1.5 rounded-lg bg-premium-gold/10 text-premium-gold text-[10px] font-bold uppercase tracking-widest hover:bg-premium-gold/20 transition-all border border-premium-gold/20"
          >
            Verify
          </button>
          <button
            onClick={() => onView(skill)}
            className="px-3 py-1.5 rounded-lg bg-white/5 text-white/70 text-[10px] font-bold uppercase tracking-widest hover:bg-white/10 transition-all border border-white/10"
          >
            Details
          </button>
        </div>
      </div>
    </div>
  );
};

const SkillManagement: React.FC<SkillManagementProps> = ({
  user,
  userSkills,
  availableSkills,
  loading: parentLoading,
  onRefresh,
  apiClient,
  searchTerm,
  selectedCategory: initialCategory
}) => {
  const [selectedCategory, setSelectedCategory] = useState(initialCategory || 'all');
  const [showAddSkill, setShowAddSkill] = useState(false);
  const [showSkillDetails, setShowSkillDetails] = useState<{ show: boolean; skill: UserSkill | null }>({
    show: false,
    skill: null
  });
  const [showAssessment, setShowAssessment] = useState<{ show: boolean; skillId: string | null }>({
    show: false,
    skillId: null
  });
  const [loading, setLoading] = useState({
    saving: false,
    assessment: false
  });
  const [error, setError] = useState('');
  const [assessments, setAssessments] = useState<Record<string, Assessment>>({});

  // Form State for Add/Edit
  const [customSkillName, setCustomSkillName] = useState('');
  const [customSkillDescription, setCustomSkillDescription] = useState('');
  const [customSkillCategory, setCustomSkillCategory] = useState('general');
  const [selectedProficiency, setSelectedProficiency] = useState<number>(3);
  const [yearsOfExperience, setYearsOfExperience] = useState<number>(1);
  const [lastUsed, setLastUsed] = useState<string>(new Date().getFullYear().toString());
  const [evidenceData, setEvidenceData] = useState<Array<{
    type: 'certification' | 'project' | 'work' | 'education';
    title: string;
    issuer?: string;
    date?: string;
    url?: string;
  }>>([]);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editingSkillId, setEditingSkillId] = useState<string | null>(null);

  const [filters] = useState({
    sortBy: 'proficiency',
    sortOrder: 'desc' as 'asc' | 'desc'
  });

  // Ensure user has a talent profile — sends real user data to backend for sync
  const createTalentProfileIfNeeded = async () => {
    if (!user) return;

    try {
      const fullName = `${(user as any).firstName || ''} ${(user as any).lastName || ''}`.trim() ||
        (user as any).name || '';

      // The backend will also auto-sync from User DB, but we send what we have
      // to cover cases where the backend lookup might fail
      await apiClient.upsertTalentProfile({
        userId: user.id,
        name: fullName || undefined,
        status: 'active',
      });
    } catch (error) {
      console.error('Error creating talent profile:', error);
      // Don't throw error if profile already exists (409 Conflict)
      if ((error as any).message?.includes('already exists') ||
        (error as any).response?.status === 409) {
        return;
      }
      throw error;
    }
  };

  // Add a new skill to user's profile (custom or from available skills)
  const handleAddSkill = async (skill?: Skill) => {
    if (!user) return;

    try {
      setLoading(prev => ({ ...prev, saving: true }));
      await createTalentProfileIfNeeded();

      // Use custom skill data if no skill is provided (custom skill mode)
      const skillToAdd = skill || {
        _id: '',
        name: customSkillName,
        description: customSkillDescription,
        category: customSkillCategory
      };

      const skillData = {
        name: skillToAdd.name,
        description: skillToAdd.description,
        category: skillToAdd.category,
        proficiency: selectedProficiency,
        yearsOfExperience: yearsOfExperience,
        lastUsed: `${lastUsed}-01-01T00:00:00.000Z`,
        endorsements: 0,
        evidence: evidenceData
      };

      // Add the skill via API
      await apiClient.addUserSkill(user.id, skillData);

      // Reset form and close modal
      resetForm();
      setShowAddSkill(false);

      // Refresh skills from parent component
      onRefresh();

      // Show success message
      setError('Skill added successfully!');
      setTimeout(() => setError(''), 3000);
    } catch (error: any) {
      console.error('Error adding skill:', error);
      setError(error.message || 'Failed to add skill. Please try again.');
    } finally {
      setLoading(prev => ({ ...prev, saving: false }));
    }
  };

  // Update existing skill
  const handleUpdateSkill = async () => {
    if (!user || !editingSkillId) return;

    try {
      setLoading(prev => ({ ...prev, saving: true }));

      const updateData = {
        proficiency: selectedProficiency,
        yearsOfExperience: yearsOfExperience,
        lastUsed: `${lastUsed}-01-01T00:00:00.000Z`,
        evidence: evidenceData
      };

      // Update the skill via API
      await apiClient.updateUserSkill(user.id, editingSkillId, updateData);

      // Reset form and close modal
      resetForm();
      setShowAddSkill(false);

      // Refresh skills from parent component
      onRefresh();

      // Show success message
      setError('Skill updated successfully!');
      setTimeout(() => setError(''), 3000);
    } catch (error: any) {
      console.error('Error updating skill:', error);
      setError(error.message || 'Failed to update skill. Please try again.');
    } finally {
      setLoading(prev => ({ ...prev, saving: false }));
    }
  };

  // Reset form function
  const resetForm = () => {
    setCustomSkillName('');
    setCustomSkillDescription('');
    setCustomSkillCategory('general');
    setSelectedProficiency(3);
    setYearsOfExperience(1);
    setLastUsed(new Date().getFullYear().toString());
    setEvidenceData([]);
    setIsEditMode(false);
    setEditingSkillId(null);
  };

  const handleEditSkill = (skill: UserSkill) => {
    setIsEditMode(true);
    setEditingSkillId(skill.id);
    setCustomSkillName(skill.name);
    setCustomSkillDescription(skill.description || '');
    setCustomSkillCategory(skill.category || 'general');
    setSelectedProficiency(skill.proficiency);
    setYearsOfExperience(skill.yearsOfExperience || 1);

    // Parse lastUsed if available
    if (skill.lastUsed) {
      try {
        setLastUsed(new Date(skill.lastUsed).getFullYear().toString());
      } catch (e) {
        setLastUsed(new Date().getFullYear().toString());
      }
    }

    setEvidenceData(skill.evidence || []);
    setShowAddSkill(true);
  };

  const handleDeleteSkill = async (skillId: string) => {
    if (!user || !window.confirm('Are you sure you want to remove this skill?')) return;

    try {
      setLoading(prev => ({ ...prev, saving: true }));
      await apiClient.deleteUserSkill(user.id, skillId);
      onRefresh();
      setError('Skill removed successfully.');
      setTimeout(() => setError(''), 3000);
    } catch (error: any) {
      console.error('Error removing skill:', error);
      setError(error.message || 'Failed to remove skill. Please try again.');
    } finally {
      setLoading(prev => ({ ...prev, saving: false }));
    }
  };

  const removeEvidence = (index: number) => {
    setEvidenceData(prev => prev.filter((_, i) => i !== index));
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    try {
      setUploadingEvidence(true);
      const response = await apiClient.uploadFile(file);

      setEvidenceData(prev => [
        ...prev,
        {
          type: 'certification',
          title: file.name,
          url: response.url,
          date: new Date().toISOString(),
          issuer: 'Self-Upload'
        }
      ]);

      setError('Proof of mastery uploaded successfully!');
      setTimeout(() => setError(''), 3000);
    } catch (err: any) {
      console.error('Upload failed:', err);
      setError('Failed to upload proof. Please try again.');
    } finally {
      setUploadingEvidence(false);
    }
  };

  // Filter and sort skills based on search and filters
  const filteredSkills = useCallback(() => {
    const query = (searchTerm ?? "").toLowerCase();
    return availableSkills
      .filter((skill: Skill) => {
        if (!skill || !skill.name) return false;

        const matchesSearch = skill.name.toLowerCase().includes(query) ||
          ((skill.description ?? "").toLowerCase().includes(query));
        const matchesCategory = selectedCategory === 'all' || skill.category === selectedCategory;

        return matchesSearch && matchesCategory;
      })
      .sort((a: Skill, b: Skill) => {
        if (filters.sortBy === 'name') {
          return filters.sortOrder === 'asc'
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name);
        }

        const userSkillA = userSkills.find(us => us.name === a.name);
        const userSkillB = userSkills.find(us => us.name === b.name);

        if (filters.sortBy === 'proficiency') {
          const profA = userSkillA?.proficiency || 0;
          const profB = userSkillB?.proficiency || 0;
          return filters.sortOrder === 'asc' ? profA - profB : profB - profA;
        }

        if (filters.sortBy === 'lastUsed' && userSkillA?.lastUsed && userSkillB?.lastUsed) {
          return filters.sortOrder === 'asc'
            ? new Date(userSkillA.lastUsed).getTime() - new Date(userSkillB.lastUsed).getTime()
            : new Date(userSkillB.lastUsed).getTime() - new Date(userSkillA.lastUsed).getTime();
        }

        return 0;
      });
  }, [availableSkills, searchTerm, selectedCategory, userSkills, filters]);

  const isSkillAdded = useCallback((skillName: string) => {
    return userSkills.some(skill => skill.name === skillName);
  }, [userSkills]);

  const handleStartAssessment = async (skillId: string) => {
    if (!user) return;

    try {
      setLoading(prev => ({ ...prev, assessment: true }));
      // Pass skillId to ensure the backend generates an assessment if missing
      const response = await apiClient.getSkillAssessments(user.id, skillId);
      const skillAssessments = response.assessments.filter((a: any) =>
        a.skillId === skillId || a.skillId?._id === skillId || a.skillId?.id === skillId
      );

      if (skillAssessments.length > 0) {
        setAssessments(prev => ({
          ...prev,
          [skillId]: skillAssessments[0]
        }));
        setShowAssessment({ show: true, skillId });
      } else {
        setError('No official assessment available for this skill yet. Our evaluators are working on it!');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err: any) {
      console.error('Error fetching assessments:', err);
      setError('Failed to load assessments. Please try again later.');
    } finally {
      setLoading(prev => ({ ...prev, assessment: false }));
    }
  };

  const handleSubmitAssessment = async (answers: Record<string, number>, skillId: string) => {
    if (!user) return;

    try {
      setLoading(prev => ({ ...prev, assessment: true }));
      const assessment = assessments[skillId];
      if (!assessment) return;

      const response = await apiClient.submitSkillAssessment(user.id, skillId, {
        assessmentId: assessment.id || assessment._id,
        answers,
        timeSpent: 0
      });

      setAssessments(prev => ({
        ...prev,
        [skillId]: {
          ...assessment,
          completed: true,
          score: response.score,
          dateCompleted: new Date().toISOString()
        }
      }));

      setShowAssessment({ show: false, skillId: null });
      setError(`Assessment complete! Score: ${response.score}%. ${response.passed ? 'Skill verified!' : 'Try again later.'}`);

      if (response.passed) {
        onRefresh();
      }

      setTimeout(() => setError(''), 5000);
    } catch (error: any) {
      console.error('Error submitting assessment:', error);
      setError(error.message || 'Failed to submit assessment');
    } finally {
      setLoading(prev => ({ ...prev, assessment: false }));
    }
  };

  const renderSkillDetails = () => {
    const skill = showSkillDetails.skill;
    if (!showSkillDetails.show || !skill) return null;

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-[#1a1510] border border-white/10 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-fade-in-up">
          <div className="px-6 py-5 border-b border-white/10 bg-gradient-to-r from-premium-gold/5 to-transparent flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-white font-serif">Skill Dossier</h3>
              <p className="text-white/40 text-[10px] uppercase tracking-[0.2em] mt-0.5">Verification & Mastery Records</p>
            </div>
            <button
              onClick={() => setShowSkillDetails({ show: false, skill: null })}
              className="text-white/30 hover:text-white p-2 hover:bg-white/5 rounded-full transition-all"
            >
              <FiX className="h-5 w-5" />
            </button>
          </div>

          <div className="px-8 py-8 space-y-8">
            <div className="flex items-start gap-6">
              <div className="w-20 h-20 bg-white/5 rounded-2xl border border-white/10 flex items-center justify-center group overflow-hidden relative">
                <div className="absolute inset-0 bg-premium-gold/5 group-hover:bg-premium-gold/10 transition-colors" />
                <div className="text-3xl text-premium-gold/80 relative z-10">
                  {skill.category && SKILL_ICONS[skill.category as keyof typeof SKILL_ICONS]}
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold text-white font-serif">{skill.name}</h2>
                  {skill.category && (
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest border"
                      style={{
                        backgroundColor: `${SKILL_CATEGORIES.find(c => c.id === skill.category)?.color}20`,
                        color: SKILL_CATEGORIES.find(c => c.id === skill.category)?.color,
                        borderColor: `${SKILL_CATEGORIES.find(c => c.id === skill.category)?.color}40`
                      }}
                    >
                      {skill.category}
                    </span>
                  )}
                </div>
                <p className="text-white/60 text-sm leading-relaxed">{skill.description || 'No description provided for this mastery.'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                <p className="text-[10px] font-bold text-premium-gold uppercase tracking-widest mb-3">Official Proficiency</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-premium-gold/10 rounded-lg flex items-center justify-center border border-premium-gold/30">
                    <FiAward className="text-premium-gold" size={24} />
                  </div>
                  <div>
                    <p className="text-white font-bold">{PROFICIENCY_LEVELS[skill.proficiency - 1]?.label}</p>
                    <p className="text-[10px] text-white/40">{PROFICIENCY_LEVELS[skill.proficiency - 1]?.description}</p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                <p className="text-[10px] font-bold text-premium-gold uppercase tracking-widest mb-3">Experience Tenure</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-white font-serif">{skill.yearsOfExperience || 0}</span>
                  <span className="text-xs text-white/40 uppercase tracking-wider">Years Active</span>
                </div>
                {skill.lastUsed && (
                  <p className="text-[10px] text-white/40 mt-1 uppercase tracking-tighter">Last deployed in {new Date(skill.lastUsed).getFullYear()}</p>
                )}
              </div>
            </div>

            {skill.evidence && skill.evidence.length > 0 ? (
              <div className="space-y-4">
                <p className="text-[10px] font-bold text-premium-gold uppercase tracking-widest">Mastery Records</p>
                <div className="grid gap-3">
                  {skill.evidence.map((ev, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-xl group transition-all hover:border-premium-gold/30">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-premium-gold/10 flex items-center justify-center border border-premium-gold/20">
                          <FiAward className="text-premium-gold" size={18} />
                        </div>
                        <div>
                          <p className="text-sm text-white font-bold">{ev.title}</p>
                          <p className="text-[10px] text-white/40 uppercase tracking-wider">{ev.issuer} • {ev.date ? new Date(ev.date).toLocaleDateString() : 'Active Record'}</p>
                        </div>
                      </div>
                      {ev.url && (
                        <a
                          href={ev.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-white/40 hover:text-premium-gold hover:bg-white/10 rounded-lg transition-all"
                        >
                          <FiExternalLink size={16} />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-6 border border-dashed border-white/10 rounded-xl flex flex-col items-center justify-center text-center">
                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mb-3">
                  <FiAward className="text-white/20" size={24} />
                </div>
                <p className="text-xs text-white/40 italic">No evidence records have been sealed for this skill yet.</p>
              </div>
            )}
          </div>

          <div className="px-6 py-5 border-t border-white/10 bg-[#1a1510] flex gap-3">
            <button
              onClick={() => {
                setShowSkillDetails({ show: false, skill: null });
                handleEditSkill(skill);
              }}
              className="flex-1 py-3 bg-white/5 text-white/70 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-white/10 transition-all border border-white/10"
            >
              Modify Record
            </button>
            <button
              onClick={() => {
                setShowSkillDetails({ show: false, skill: null });
                handleStartAssessment(skill.id);
              }}
              className="flex-1 py-3 bg-premium-gold text-[#1a1510] rounded-xl text-xs font-bold uppercase tracking-widest hover:shadow-[0_0_15px_rgba(212,175,55,0.3)] transition-all"
            >
              Obtain Validation
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAssessmentModal = () => {
    const skillId = showAssessment.skillId;
    if (!skillId) return null;
    const assessment = assessments[skillId];
    if (!assessment) return null;

    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 z-[60] animate-fade-in">
        <div className="bg-[#1a1510] border border-premium-gold/30 rounded-2xl shadow-[0_0_50px_rgba(212,175,55,0.15)] w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col animate-fade-in">
          <div className="px-6 py-6 border-b border-white/10 bg-gradient-to-b from-premium-gold/10 to-transparent flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-premium-gold/10 rounded-xl flex items-center justify-center border border-premium-gold/30">
                <FiShield className="text-premium-gold" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white font-serif uppercase tracking-widest">
                  Executive Mastery Board
                </h3>
                <p className="text-premium-gold/60 text-[10px] font-bold uppercase tracking-[0.2em] mt-0.5">
                  Official Technical Evaluation: {assessment.skillName}
                </p>
              </div>
            </div>
            <div className="px-3 py-1 bg-premium-gold/20 rounded border border-premium-gold/40">
              <span className="text-premium-gold text-[10px] font-bold uppercase tracking-widest">
                {assessment.level || 'Expert'} Level
              </span>
            </div>
          </div>

          <div className="p-8 overflow-y-auto flex-1 space-y-8">
            {assessment.questions.map((q, qIdx) => (
              <div key={q.id} className="space-y-5">
                <div className="flex gap-4">
                  <span className="text-premium-gold/40 font-serif text-2xl font-bold">0{qIdx + 1}</span>
                  <p className="text-lg text-white font-serif leading-relaxed pt-1">{q.question}</p>
                </div>
                <div className="grid gap-3 pl-12">
                  {q.options.map((option, oIdx) => (
                    <button
                      key={oIdx}
                      onClick={() => {
                        const newAnswers = { ...(assessments[skillId].score as any || {}), [q.id]: oIdx };
                        // Note: This logic for temporary answers is simplified for storage
                        setAssessments(prev => ({
                          ...prev,
                          [skillId]: { ...prev[skillId], score: newAnswers as any }
                        }));
                      }}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${(assessments[skillId].score as any)?.[q.id] === oIdx
                        ? 'border-premium-gold bg-premium-gold/10 text-white'
                        : 'border-white/5 bg-white/5 text-white/50 hover:border-white/20 hover:bg-white/10'
                        }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${(assessments[skillId].score as any)?.[q.id] === oIdx ? 'border-premium-gold' : 'border-white/20'
                          }`}>
                          {(assessments[skillId].score as any)?.[q.id] === oIdx && (
                            <div className="w-2.5 h-2.5 rounded-full bg-premium-gold shadow-[0_0_8px_rgba(212,175,55,0.6)]" />
                          )}
                        </div>
                        <span className="text-sm font-medium">{option}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="px-8 py-6 border-t border-white/10 bg-[#1a1510] flex gap-4">
            <button
              onClick={() => setShowAssessment({ show: false, skillId: null })}
              className="px-6 py-3 text-sm font-bold text-white/40 hover:text-white uppercase tracking-widest transition-all"
            >
              Abort Mission
            </button>
            <button
              onClick={() => {
                const answers = assessments[skillId].score as any || {};
                handleSubmitAssessment(answers, skillId);
              }}
              disabled={loading.assessment}
              className="flex-1 py-3 bg-gradient-to-r from-premium-gold-dark to-premium-gold text-[#1a1510] rounded-xl font-bold uppercase tracking-widest text-sm hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all disabled:opacity-50"
            >
              {loading.assessment ? 'Processing Evaluation...' : 'Submit to Board for Review'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-transparent">
      {/* Error Message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed top-24 left-1/2 -translate-x-1/2 px-6 py-3 rounded-xl border z-50 shadow-2xl ${error.includes('successfully') || error.includes('verified')
              ? 'bg-green-500/10 border-green-500/50 text-green-400'
              : 'bg-red-500/10 border-red-500/50 text-red-400'
              }`}
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div>
            <h1 className="text-4xl font-bold text-white font-serif mb-2 uppercase tracking-tight">Skill Management</h1>
            <p className="text-white/40 max-w-2xl font-medium">Curate and verify your professional capabilities within the TechUnity ecosystem.</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setIsEditMode(false);
              setShowAddSkill(true);
            }}
            className="flex items-center gap-2 px-6 py-3 bg-premium-gold text-[#1a1510] rounded-xl font-bold uppercase tracking-widest text-sm hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all transform hover:scale-105"
          >
            <FiPlus size={18} />
            Seal New Mastery
          </button>
        </div>

        {/* User Skills Grid */}
        <div className="mb-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl font-bold text-white font-serif uppercase tracking-widest border-l-4 border-premium-gold pl-4">Your Sealed Masteries</h2>
            <div className="flex items-center gap-4">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-sm text-white focus:border-premium-gold/50 outline-none transition-all"
              >
                {SKILL_CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id} className="bg-[#1a1510]">{cat.name}</option>
                ))}
              </select>
            </div>
          </div>

          {parentLoading.skills ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-48 bg-white/5 rounded-xl border border-white/10" />
              ))}
            </div>
          ) : userSkills.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {userSkills
                .filter(s => selectedCategory === 'all' || s.category === selectedCategory)
                .map((skill) => (
                  <SkillCard
                    key={skill.id}
                    skill={skill}
                    onEdit={handleEditSkill}
                    onDelete={handleDeleteSkill}
                    onView={(skill) => setShowSkillDetails({ show: true, skill })}
                    onAssessment={handleStartAssessment}
                  />
                ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-white/5 border border-dashed border-white/10 rounded-2xl">
              <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiAward className="text-white/20" size={32} />
              </div>
              <p className="text-white/40 font-serif italic">Your journey of mastery begins here. No skills sealed yet.</p>
              <button
                onClick={() => setShowAddSkill(true)}
                className="mt-6 text-premium-gold text-sm font-bold uppercase tracking-widest hover:underline"
              >
                Initialize Your First Mastery →
              </button>
            </div>
          )}
        </div>

        {/* Available Skills Discovery */}
        <div className="mb-12">
          <h2 className="text-xl font-bold text-white font-serif uppercase tracking-widest border-l-4 border-white/20 pl-4 mb-8">Discover New Specializations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {filteredSkills()
              .filter(skill => !isSkillAdded(skill.name))
              .slice(0, 8)
              .map((skill) => (
                <div
                  key={skill._id}
                  className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-premium-gold/30 hover:bg-white/10 transition-all cursor-pointer group"
                  onClick={() => {
                    resetForm();
                    setCustomSkillName(skill.name);
                    setCustomSkillDescription(skill.description);
                    setCustomSkillCategory(skill.category);
                    setShowAddSkill(true);
                  }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl opacity-60 group-hover:opacity-100 transition-opacity">
                      {SKILL_ICONS[skill.category as keyof typeof SKILL_ICONS] || '✨'}
                    </span>
                    <h3 className="font-bold text-white text-sm uppercase tracking-wide">{skill.name}</h3>
                  </div>
                  <p className="text-[11px] text-white/40 line-clamp-2 leading-relaxed">
                    {skill.description}
                  </p>
                  <div className="mt-4 flex justify-end">
                    <span className="text-[10px] text-premium-gold font-bold uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0">
                      Acquire Skill +
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Add/Edit Skill Modal */}
      {showAddSkill && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/85 backdrop-blur-md"
            onClick={() => {
              resetForm();
              setShowAddSkill(false);
            }}
          />
          <div className="relative w-full max-w-4xl max-h-[92vh] mt-12 sm:mt-0 bg-[#1a1510] border border-premium-gold/30 rounded-2xl shadow-[0_30px_100px_rgba(0,0,0,0.8)] overflow-hidden flex flex-col animate-fade-in-up">
            <div className="flex-shrink-0 px-8 py-6 border-b border-white/10 bg-[#241e16] flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white font-serif uppercase tracking-tight">
                  {isEditMode ? 'Refine Mastery' : 'Seal New Mastery'}
                </h2>
                <p className="text-white/40 text-[10px] uppercase tracking-[0.2em] mt-1 font-bold">Documenting Your Competitive Edge</p>
              </div>
              <button
                onClick={() => {
                  resetForm();
                  setShowAddSkill(false);
                }}
                className="p-2 text-white/30 hover:text-white transition-colors"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="p-8 overflow-y-auto flex-1 custom-scrollbar">
              <div className="grid md:grid-cols-2 gap-10">
                {/* Left Column: Core Identity */}
                <div className="space-y-8">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-premium-gold uppercase tracking-[0.2em]">Mastery Title</label>
                    <input
                      type="text"
                      value={customSkillName}
                      onChange={(e) => setCustomSkillName(e.target.value)}
                      placeholder="e.g. Tactical Strategic Planning"
                      disabled={isEditMode}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-premium-gold/50 outline-none transition-all placeholder:text-white/10 font-bold"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-premium-gold uppercase tracking-[0.2em]">Specialization Realm</label>
                    <div className="flex flex-wrap gap-2">
                      {SKILL_CATEGORIES.filter(c => c.id !== 'all').map(cat => (
                        <button
                          key={cat.id}
                          onClick={() => setCustomSkillCategory(cat.id)}
                          className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-all ${customSkillCategory === cat.id
                            ? 'bg-premium-gold text-[#1a1510] border border-premium-gold'
                            : 'bg-white/5 text-white/40 border border-white/10 hover:border-white/20'
                            }`}
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-premium-gold uppercase tracking-[0.2em]">Mastery Description</label>
                    <textarea
                      value={customSkillDescription}
                      onChange={(e) => setCustomSkillDescription(e.target.value)}
                      placeholder="Describe the depth of your expertise..."
                      rows={4}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-premium-gold/50 outline-none transition-all placeholder:text-white/10 text-sm leading-relaxed"
                    />
                  </div>
                </div>

                {/* Right Column: Proficiency & Proof */}
                <div className="space-y-8">
                  <div className="space-y-4">
                    <label className="text-[10px] font-bold text-premium-gold uppercase tracking-[0.2em]">Current Proficiency Level</label>
                    <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-5 gap-2">
                      {PROFICIENCY_LEVELS.map((level) => (
                        <button
                          key={level.value}
                          onClick={() => setSelectedProficiency(level.value)}
                          className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-all ${selectedProficiency === level.value
                            ? 'border-premium-gold bg-premium-gold/10'
                            : 'border-white/5 bg-white/5 hover:border-white/10'
                            }`}
                        >
                          <div className={`p-1.5 rounded-lg bg-white/5 ${selectedProficiency === level.value ? 'text-premium-gold' : 'text-white/20'}`}>
                            <FiStar size={16} className={selectedProficiency === level.value ? 'fill-premium-gold' : ''} />
                          </div>
                          <span className={`text-[8px] font-bold uppercase tracking-widest ${selectedProficiency === level.value ? 'text-white' : 'text-white/30'}`}>
                            {level.label}
                          </span>
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-white/40 italic px-1 pt-1 border-t border-white/5">
                      {PROFICIENCY_LEVELS[selectedProficiency - 1].description}
                    </p>
                  </div>

                  <div className="flex flex-col sm:grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-premium-gold uppercase tracking-[0.2em]">Years Active</label>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        value={yearsOfExperience}
                        onChange={(e) => setYearsOfExperience(parseInt(e.target.value))}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-premium-gold/50 outline-none transition-all font-bold"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-premium-gold uppercase tracking-[0.2em]">Year of Last Use</label>
                      <input
                        type="number"
                        min="2000"
                        max={new Date().getFullYear()}
                        value={lastUsed}
                        onChange={(e) => setLastUsed(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-premium-gold/50 outline-none transition-all font-bold"
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] font-bold text-premium-gold uppercase tracking-[0.2em]">Proofs of Mastery</label>
                      <span className="text-[10px] text-white/20 font-bold uppercase tracking-widest">{evidenceData.length} Attached</span>
                    </div>

                    <div className="space-y-2">
                      {evidenceData.map((ev, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-white/5 border border-white/10 rounded-xl group transition-all">
                          <div className="flex items-center gap-3">
                            <FiAward className="text-premium-gold/50" />
                            <span className="text-xs text-white/70 font-medium truncate max-w-[150px]">{ev.title}</span>
                          </div>
                          <button
                            onClick={() => removeEvidence(idx)}
                            className="text-white/20 hover:text-red-400 p-1 transition-colors"
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="relative">
                      <input
                        type="file"
                        id="evidence-upload"
                        className="hidden"
                        onChange={handleFileUpload}
                        disabled={uploadingEvidence}
                      />
                      <label
                        htmlFor="evidence-upload"
                        className={`flex flex-col items-center justify-center gap-1.5 w-full py-4 border-2 border-dashed border-white/10 rounded-xl cursor-pointer hover:border-premium-gold/30 hover:bg-white/5 transition-all ${uploadingEvidence ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <FiPlus className="text-white/30" />
                        <span className="text-[10px] text-white/40 uppercase tracking-widest font-bold">
                          {uploadingEvidence ? 'Verifying...' : 'Attach Proof of Mastery'}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-8 py-6 border-t border-white/10 bg-[#1a1510] flex gap-4">
              <button
                onClick={() => {
                  resetForm();
                  setShowAddSkill(false);
                }}
                className="flex-1 py-3 text-sm font-bold text-white/40 hover:text-white transition-all uppercase tracking-widest"
              >
                Cancel
              </button>
              <button
                onClick={() => isEditMode ? handleUpdateSkill() : handleAddSkill()}
                disabled={loading.saving || !customSkillName}
                className="flex-[2] py-4 bg-gradient-to-r from-premium-gold-dark to-premium-gold text-[#1a1510] rounded-xl font-bold uppercase tracking-widest text-sm hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all transform hover:scale-[1.01] disabled:opacity-50 disabled:grayscale"
              >
                {loading.saving ? 'Processing...' : (isEditMode ? 'Apply Refinements' : 'Seal In Records')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {renderSkillDetails()}
      {showAssessment.show && renderAssessmentModal()}
    </div>
  );
};

export default SkillManagement;
